import { useEffect, useMemo, useRef, useState } from 'react'

import { GACHA_EVENT_ID } from '@/constants'
import { useGetGachaRecentWinners } from '@/query/gacha/useGetGachaRecentWinners'

import { formatTimeAgo } from './formatTimeAgo'
import {
  TickerDivider,
  TickerPill,
  TimeText,
  WinnerAmount,
  WinnerInfo,
  WinnerLabel,
  WinnerName,
  Wrapper,
} from './styles'

const TICKER_INTERVAL_MS = 7000
const TRANSITION_MS = 350
const WINNERS_PAGE_SIZE = 50
const REFETCH_INTERVAL_MS = 5 * 60 * 1000

export default function FooterSection() {
  const gachaEventId = Number(GACHA_EVENT_ID)
  const { data: winners, isLoading } = useGetGachaRecentWinners(gachaEventId, {
    size: WINNERS_PAGE_SIZE,
    refetchInterval: REFETCH_INTERVAL_MS,
  })

  // 시간 표시가 가능한(=10분 이내, 파싱 가능한) 당첨자만 노출.
  // 그 외(파싱 실패·만료)는 카드 자체에서 제외.
  const displayableWinners = useMemo(
    () => (winners ?? []).filter((winner) => formatTimeAgo(winner.createdAt) !== null),
    [winners]
  )

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isExiting, setIsExiting] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const winnersCount = displayableWinners.length

  useEffect(() => {
    if (winnersCount === 0) return

    intervalRef.current = setInterval(() => {
      setIsExiting(true)

      timeoutRef.current = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % winnersCount)
        setIsExiting(false)
      }, TRANSITION_MS)
    }, TICKER_INTERVAL_MS)

    return () => {
      clearInterval(intervalRef.current)
      clearTimeout(timeoutRef.current)
    }
  }, [winnersCount])

  // 로딩 중 또는 표시 가능한 당첨자 없음 → ticker 자체 숨김
  if (isLoading || displayableWinners.length === 0) return null

  const winner = displayableWinners[currentIndex % displayableWinners.length]
  const timeAgo = formatTimeAgo(winner.createdAt)

  return (
    <Wrapper>
      <TickerPill
        style={{
          opacity: isExiting ? 0 : 1,
          transform: isExiting ? 'translateY(-6px)' : 'translateY(0)',
          transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
        }}
      >
        <WinnerInfo>
          <WinnerName>{winner.maskedName}</WinnerName>
          <WinnerAmount>{winner.rewardName}</WinnerAmount>
          <WinnerLabel>당첨</WinnerLabel>
        </WinnerInfo>
        {timeAgo !== null && (
          <>
            <TickerDivider>|</TickerDivider>
            <TimeText>{timeAgo}</TimeText>
          </>
        )}
      </TickerPill>
    </Wrapper>
  )
}
